/**
 * Component utilities - Flow component metadata utilities
 *
 * This file provides utility functions for accessing component metadata
 * from the flow component registry.
 */

import { flowComponentRegistry } from './FlowComponentRegistry';

/**
 * Get all flow components
 */
export const getAllComponentDefinitions = () => {
  return flowComponentRegistry.getAll();
};

/**
 * Get flow component by type
 */
export const getComponentDefinition = (componentType) => {
  return flowComponentRegistry.get(componentType);
};

/**
 * Get component icon by type
 */
export const getComponentIcon = (componentType) => {
  const flowComponent = flowComponentRegistry.get(componentType);
  return flowComponent?.icon || '📦';
};

/**
 * Get component name by type
 */
export const getComponentName = (componentType) => {
  const flowComponent = flowComponentRegistry.get(componentType);
  return flowComponent?.name || componentType;
};

/**
 * Get component max per screen limit
 */
export const getComponentMaxPerScreen = (componentType) => {
  const flowComponent = flowComponentRegistry.get(componentType);
  return flowComponent?.maxPerScreen;
};

/**
 * Get component default properties
 */
export const getComponentDefaultProperties = (componentType) => {
  const flowComponent = flowComponentRegistry.get(componentType);
  return flowComponent?.getDefaultProperties() || {};
};

/**
 * Get component property schema
 */
export const getComponentPropertySchema = (componentType) => {
  const flowComponent = flowComponentRegistry.get(componentType);
  return flowComponent?.getPropertySchema() || {};
};

/**
 * Check if component requires name field
 */
export const componentRequiresName = (componentType) => {
  const schema = getComponentPropertySchema(componentType);
  return schema?.name?.required === true;
};

/**
 * Get components that require name field
 */
export const getComponentsWithNameField = () => {
  return flowComponentRegistry
    .getAll()
    .filter((flowComponent) => componentRequiresName(flowComponent.type))
    .map((flowComponent) => flowComponent.type);
};

/**
 * Get form components (components that collect user input)
 */
export const getFormComponents = () => {
  // Get all input and selection components
  const inputComponents = flowComponentRegistry.getByCategory('input');
  const selectionComponents = flowComponentRegistry.getByCategory('selection');

  return [...inputComponents, ...selectionComponents].map((flowComponent) => flowComponent.type);
};

/**
 * Check if component is a form component
 */
export const isFormComponent = (componentType) => {
  return getFormComponents().includes(componentType);
};

/**
 * Validate component against its flow component definition.
 * `context` is forwarded to the component's validator, e.g. { screens } lets a Footer
 * verify its navigate target still points at an existing screen.
 */
export const validateComponent = (component, context = {}) => {
  const flowComponent = flowComponentRegistry.get(component.type);
  if (!flowComponent) {
    return {
      errors: [`Unknown component type: ${component.type}`],
      warnings: [],
    };
  }

  return flowComponent.validateProperties(component.properties || {}, context);
};

/**
 * Check if a component can be added to a screen
 */
export const canAddComponentToScreen = (componentType, screen) => {
  const flowComponent = flowComponentRegistry.get(componentType);
  if (!flowComponent) {
    return false;
  }

  return flowComponent.canAddToScreen(screen);
};

/**
 * Get message explaining why component cannot be added to screen
 */
export const getAddBlockerMessage = (componentType, screen) => {
  const flowComponent = flowComponentRegistry.get(componentType);
  if (!flowComponent) {
    return `Unknown component type: ${componentType}`;
  }

  return flowComponent.getAddBlockerMessage(screen);
};
